import { Field, FormDefinition } from "../vforms-model";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TextBox } from "../field-types/text-box/text-box";
import { assign } from "lodash-es";
import { NumberField } from "../field-types/number/number";
import { Textarea } from "../field-types/textarea/text-area";
import { EmailField } from "../field-types/email/email";
import { SelectionList, SelectionListOrientation } from "../field-types/selection-list";
import { Datetime } from "../field-types/datetime/datetime";
import { AbstractControl, ValidationErrors } from "@angular/forms";
import { CheckBox } from "../field-types/check-box/check-box";
import { Cid10Field } from "../field-types/cid10/cid10";
import { FileUploaderField, FileUploaderType } from "../field-types/file-uploader/file-uploader";
import { Action, Conditional } from "../expression-settings/model";
import { get } from "lodash";

const EMAIL_REGEXP =
    /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export function EmailValidator(control: AbstractControl, field: FormlyFieldConfig, options?: any): ValidationErrors {
    return !control.value || EMAIL_REGEXP.test(control.value) ? null : { email: true };
}

export class FieldConverter<T extends Field> {
    convert(field: T): FormlyFieldConfig {
        return {
            key: field.id,
            templateOptions: {
                required: field.required,
                description: field.description,
            },
        };
    }
}

export class TextBoxConverter extends FieldConverter<TextBox> {
    convert(field: TextBox): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: "input",
        });
        assign(config.templateOptions, {
            placeholder: field.placeholder,
        });
        return config;
    }
}

export class CheckBoxConverter extends FieldConverter<CheckBox> {
    convert(field: CheckBox): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: "checkbox",
            defaultValue: false,
        });
        assign(config.templateOptions, {
            label: field.label,
        });
        return config;
    }
}

export class TextareaConverter extends FieldConverter<Textarea> {
    convert(field: Textarea): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: "textarea",
        });
        assign(config.templateOptions, {
            placeholder: field.placeholder,
            rows: 1,
            autosize: true,
            autosizeMinRows: 1,
            autosizeMaxRows: 5,
        });
        return config;
    }
}

export class NumberFieldConverter extends FieldConverter<NumberField> {
    convert(field: NumberField): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: "input",
        });
        assign(config.templateOptions, {
            type: "text",
            placeholder: field.placeholder,
            min: field.min,
            max: field.max,
        });
        return config;
    }
}

export class EmailFieldConverter extends FieldConverter<EmailField> {
    convert(field: EmailField): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: "input",
            validators: {
                validation: ["email"],
            },
        });
        assign(config.templateOptions, {
            type: "email",
            placeholder: field.placeholder,
        });
        return config;
    }
}

export class MultipleSelectionListFieldConverter extends FieldConverter<SelectionList> {
    convert(field: SelectionList): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: "multicheckbox",
            className: field.orientation === SelectionListOrientation.Vertical ? "vertical" : "horizontal",
        });
        assign(config.templateOptions, {
            placeholder: field.placeholder,
            options: field.options.map((o) => {
                return {
                    value: o.value,
                    label: o.text,
                };
            }),
        });
        return config;
    }
}

export class SingleSelectionListFieldConverter extends FieldConverter<SelectionList> {
    convert(field: SelectionList): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: "radio",
            className: field.orientation === SelectionListOrientation.Vertical ? "vertical" : "horizontal",
        });
        assign(config.templateOptions, {
            placeholder: field.placeholder,
            options: field.options.map((o) => {
                return {
                    value: o.value,
                    label: o.text,
                };
            }),
        });
        return config;
    }
}

export class SingleSelectionDropdownFieldConverter extends FieldConverter<SelectionList> {
    convert(field: SelectionList): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: "select",
        });
        assign(config.templateOptions, {
            placeholder: field.placeholder,
            options: field.options.map((o) => {
                return {
                    value: o.value,
                    label: o.text,
                };
            }),
        });
        return config;
    }
}

export class MultipleSelectionDropdownFieldConverter extends FieldConverter<SelectionList> {
    convert(field: SelectionList): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: "select",
        });
        assign(config.templateOptions, {
            placeholder: field.placeholder,
            multiple: true,
            options: field.options.map((o) => {
                return {
                    value: o.value,
                    label: o.text,
                };
            }),
        });
        return config;
    }
}

export class DatetimeConverter extends FieldConverter<Datetime> {
    convert(field: Datetime): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: "datepicker",
        });
        assign(config.templateOptions, {
            placeholder: field.placeholder,
        });
        return config;
    }
}

export class Cid10Converter extends FieldConverter<Cid10Field> {
    convert(field: Cid10Field): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: "cid10",
        });
        assign(config.templateOptions, {
            placeholder: field.placeholder,
        });
        return config;
    }
}

export class FileUploaderConverter extends FieldConverter<FileUploaderField> {
    convert(field: FileUploaderField): FormlyFieldConfig {
        const config = super.convert(field);
        assign(config, {
            type: FileUploaderType.Id,
            templateOptions: {
                maxNumberOfFiles: field.maxNumberOfFiles,
                fileTypes: field.fileTypes,
            },
        });
        assign(config.templateOptions, {
            placeholder: field.placeholder,
            required: field.required,
        });
        return config;
    }
}

export class FieldVisibilityEvaluatorBuilder {
    field: Field;

    conditionals: Conditional[];

    constructor(field: Field, conditionals: Conditional[]) {
        this.field = field;
        this.conditionals = conditionals.map((conditional) => {
            return assign(new Conditional(), {
                ...conditional,
                positiveActions: conditional.positiveActions.filter((a) => a.field?.id === field.id),
                negativeActions: conditional.negativeActions.filter((a) => a.field?.id === field.id),
            });
        });
    }

    evaluator(model: any, formState: any, field: FormlyFieldConfig) {
        let evaluatorBuilder = get(field, "visibilityEvaluatorBuilder") as FieldVisibilityEvaluatorBuilder;
        let actions = [];
        for (let conditional of evaluatorBuilder.conditionals as Conditional[]) {
            let predicateResult = false;
            let action = Action.Display;
            for (let expression of conditional.predicate.expressions) {
                let expressionResult = true;
                for (let condition of expression.conditions) {
                    let val = model[`${condition.field?.id}`];
                    let conditionResult = condition.field?.type?.evaluate(condition, val) ?? false;
                    expressionResult &&= conditionResult;
                    if (!conditionResult) {
                        break;
                    }
                }
                predicateResult ||= expressionResult;
            }

            if (predicateResult) {
                for (let positiveAction of conditional.positiveActions) {
                    action = positiveAction.action;
                }
            } else {
                for (let negativeAction of conditional.negativeActions) {
                    action = negativeAction.action;
                }
            }

            actions.push(action);
        }

        let first = actions[0];
        return first == Action.Hide;
    }
}

export class FieldStatusEvaluatorBuilder {
    field: Field;

    conditionals: Conditional[];

    constructor(field: Field, conditionals: Conditional[]) {
        this.field = field;
        this.conditionals = conditionals.map((conditional) => {
            return assign(new Conditional(), {
                ...conditional,
                positiveActions: conditional.positiveActions.filter((a) => a.field?.id === field.id),
                negativeActions: conditional.negativeActions.filter((a) => a.field?.id === field.id),
            });
        });
    }

    evaluator(model: any, formState: any, field: FormlyFieldConfig) {
        let evaluatorBuilder = get(field, "statusEvaluatorBuilder") as FieldStatusEvaluatorBuilder;
        let actions = [];
        for (let conditional of evaluatorBuilder.conditionals as Conditional[]) {
            let predicateResult = false;
            let action = Action.Enable;
            for (let expression of conditional.predicate.expressions) {
                let expressionResult = true;
                for (let condition of expression.conditions) {
                    let val = model[`${condition.field?.id}`];
                    let conditionResult = condition.field?.type?.evaluate(condition, val) ?? false;
                    expressionResult &&= conditionResult;
                    if (!conditionResult) {
                        break;
                    }
                }
                predicateResult ||= expressionResult;
            }

            if (predicateResult) {
                for (let positiveAction of conditional.positiveActions) {
                    action = positiveAction.action;
                }
            } else {
                for (let negativeAction of conditional.negativeActions) {
                    action = negativeAction.action;
                }
            }

            actions.push(action);
        }

        let first = actions[0];
        return first == Action.Disable;
    }
}

export class ExpressionEvaluatorBuilder {
    _visibilityActions = [Action.Hide, Action.Display];

    _fieldVisibilityMap: Map<string, Conditional[]>;

    _statusActions = [Action.Enable, Action.Disable];

    _fieldStatusMap: Map<string, Conditional[]>;

    constructor(formDefinition: FormDefinition) {
        this._fieldVisibilityMap = new Map<string, Conditional[]>();
        this._fieldStatusMap = new Map<string, Conditional[]>();

        for (let f of formDefinition.fields) {
            let visibilityConditionals = formDefinition.conditionals.filter(
                (c) =>
                    c.positiveActions.findIndex(
                        (a) => a.field?.id === f.id && this._visibilityActions.includes(a.action)
                    ) >= 0 ||
                    c.negativeActions.findIndex(
                        (a) => a.field?.id === f.id && this._visibilityActions.includes(a.action)
                    ) >= 0
            );
            if (visibilityConditionals.length > 0) {
                this._fieldVisibilityMap.set(f.id, visibilityConditionals);
            }

            let statusConditionals = formDefinition.conditionals.filter(
                (c) =>
                    c.positiveActions.findIndex(
                        (a) => a.field?.id === f.id && this._statusActions.includes(a.action)
                    ) >= 0 ||
                    c.negativeActions.findIndex(
                        (a) => a.field?.id === f.id && this._statusActions.includes(a.action)
                    ) >= 0
            );
            if (statusConditionals.length > 0) {
                this._fieldStatusMap.set(f.id, statusConditionals);
            }
        }
    }

    buildStatusEvaluator(field: Field) {
        if (this._fieldStatusMap.has(field.id)) {
            let conditionals = this._fieldStatusMap.get(field.id);
            return new FieldStatusEvaluatorBuilder(field, conditionals);
        }

        return null;
    }

    buildVisibilityEvaluator(field: Field) {
        if (this._fieldVisibilityMap.has(field.id)) {
            let conditionals = this._fieldVisibilityMap.get(field.id);
            return new FieldVisibilityEvaluatorBuilder(field, conditionals);
        }

        return null;
    }
}
